import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, type WritableSignal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators, type FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { AuthService } from '../../../services/auth.service';
import { LayoutService } from '../../../services/layout.service';
import { Brand, FaviconUrl, SystemTitle } from '../../../interfaces/application';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { generatePassword } from '../../../shared/functions/password';
import type { HttpErrorResponse } from '@angular/common/http';
import { CardHeader } from '../../../shared/components/card/card-header/card-header';

@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MessageModule,
    PasswordModule,
    ButtonModule,
    CardHeader
  ],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.css'
})
export class ResetPassword {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _router: Router = inject(Router);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _layoutService: LayoutService = inject(LayoutService);

  protected brand: Brand = Brand.LOGAME;

  public code: WritableSignal<string> = signal<string>('');
  public isValidCode: WritableSignal<boolean> = signal<boolean>(false);

  public loading: WritableSignal<boolean> = signal<boolean>(false);

  public form: FormGroup = this._fb.group({
    password: [null, [Validators.required]],
  });

  public password = toSignal(this.form.controls['password'].valueChanges, {
    initialValue: this.form.controls['password'].value ?? '',
  });

  public hasMinLength = computed(() => this.password()?.length >= 8);
  public hasUpperAndLower = computed(() => /[A-Z]/.test(this.password()) && /[a-z]/.test(this.password()));
  public hasNumber = computed(() => /\d/.test(this.password()));
  public hasSpecialChar = computed(() => /[!@#$%^&*()_\-+={}[\]|:;"'<>,.?/~`]/.test(this.password()));

  public passwordValidators = [
    {
      valid: this.hasMinLength,
      message: 'A senha deve ter pelo menos 8 caracteres'
    },
    {
      valid: this.hasUpperAndLower,
      message: 'A senha deve conter pelo menos uma letra maiúscula e uma minúscula'
    },
    {
      valid: this.hasNumber,
      message: 'A senha deve conter pelo menos um número'
    },
    {
      valid: this.hasSpecialChar,
      message: 'A senha deve conter pelo menos um caractere especial'
    }
  ];

  public ngOnInit(): void {
    const { code } = this._activatedRoute.snapshot.params;
    this.code.set(code);

    this._checkCode(code);
  }

  private _toggleLoading(): void {
    this.loading.set(!this.loading());
  }

  private _checkCode(code: string): void {
    this._toggleLoading();
    this._authService.checkCode(code)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.isValidCode.set(true);

            this.form.patchValue(response.data)
            this.brand = response.data.brand;
            this._layoutService.initUpdateColors(response.data.brand);

            switch (response.data.brand) {
              case Brand.LOGAME:
                this._layoutService.changeFavicon(FaviconUrl.LOGAME);
                this._layoutService.setSystemTitle(SystemTitle.LOGAME);
                break;
              case Brand.B2XBET:
                this._layoutService.changeFavicon(FaviconUrl.B2XBET);
                this._layoutService.setSystemTitle(SystemTitle.B2XBET);
                break;
              case Brand.LIDERBET:
                this._layoutService.changeFavicon(FaviconUrl.LIDERBET);
                this._layoutService.setSystemTitle(SystemTitle.LIDERBET);
                break;
              case Brand.GERALBETR2M:
              case Brand.GERALBET:
                this._layoutService.changeFavicon(FaviconUrl.GERALBET);
                this._layoutService.setSystemTitle(SystemTitle.GERALBET);
                break;
              
            }
          }
        },
      })
  }

  public generateStrongPassword(): void {
    const password = generatePassword();
    this.form.patchValue({ password });
  }

  public submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsDirty();
      return;
    }

    this._toggleLoading();

    this._authService.resetPassword(this.code(), this.form.getRawValue())
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: 'Senha atualizada com sucesso',
              life: 3000
            });

            this.goToLogin();
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao tentar recuperar senha',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public goToLogin(): void {
    this._router.navigate(['auth', 'login']);
  }

}
